import styled from "@emotion/styled";
import { FC, useState } from "react";

import { DesignToken, IPickerListItem, Picker, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { ALL_TEAMS_ID, ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

interface IProps {
  teams: ITeam[];
  selectedTeamId: string;
  includeAll?: boolean;
  onTeamSelected(teamId: string): void;
}

const getTeamPickerListItem = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    listItemBody: <TeamPickerListItem name={team.name} pickerType="single" isDefault={team.isDefault} />,
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="single" />,
  },
});

function getTeams(teams: ITeam[], includeAll: boolean): IPickerListItem[] {
  const result: IPickerListItem[] = teams.map(team => getTeamPickerListItem(team));

  if (includeAll) {
    return [
      {
        key: ALL_TEAMS_ID,
        name: "All teams",
      },
      ...result,
    ];
  }

  return result;
}

const SettingsTeamSelection: FC<IProps> = ({
  teams,
  selectedTeamId = ALL_TEAMS_ID,
  includeAll = false,
  onTeamSelected,
}) => {
  const [selectedTeams, setSelectedTeams] = useState<IPickerListItem[]>(
    selectedTeamId === ALL_TEAMS_ID
      ? [{ key: ALL_TEAMS_ID, name: "All teams" }]
      : [getTeamPickerListItem(teams.find(team => team.id === selectedTeamId) as ITeam)]
  );

  const items = getTeams(teams, includeAll);

  return (
    <Container>
      <Text>Setup for</Text>
      <Flex width="320px">
        <Picker
          size="compact"
          hideClearButton
          data-testid="role-dropdown"
          options={items}
          selected={selectedTeams}
          onSelect={(selected: IPickerListItem[] | null) => {
            if (!selected) {
              setSelectedTeams([]);
              onTeamSelected(selectedTeamId);
            } else {
              setSelectedTeams(selected);
              onTeamSelected(selected[0].key);
            }
          }}
        />
      </Flex>
    </Container>
  );
};

const Container = styled(Flex)`
  gap: ${withVar(SpacingToken.Spacing3)};
  min-height: 34px;
  align-items: center;
  padding: ${withVar(SpacingToken.Spacing2)} ${withVar(SpacingToken.Spacing5)};
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
`;

export default SettingsTeamSelection;
